import { NetworkStatus, useQuery } from "@apollo/client";
import {
  Button,
  ControlledFacetedFilterControl,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  FacetedFilterSummaryContent,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  type Status,
  StatusBase,
  StatusUI,
  statuses,
  Txt,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import { neverFunc } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { differenceInDays, differenceInHours } from "date-fns";
import { type ReactNode, Suspense, useMemo } from "react";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { EntityLink } from "~/components/link/EntityLink/EntityLink";
import { BusinessEntityColumnCell } from "~/components/tables/BusinessEntityColumnCell";
import { graphql } from "~/gql/client";
import {
  externalBankAccountDataSourceValues,
  externalConnectionStatusValues,
} from "~/gql/client/enumValues";
import type {
  AdminExternalConnectionRowFragment,
  ExternalBankAccountDataSource,
  ExternalConnectionsOrderBy,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/external-connections")({
  head: () => opsHead("External Connections"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const connectionSources = externalBankAccountDataSourceValues.filter(
  (s) => s !== "MANUAL_ENTRY",
);

const sortOrders = [
  "CONNECTED_AT_DESC",
  "CONNECTED_AT_ASC",
  "EXPIRES_AT_ASC",
  "EXPIRES_AT_DESC",
] as const;
type SortOrder = (typeof sortOrders)[number];

function View() {
  const { useSearchParams } = useRouter();

  const { data: organisationsData } = useQuery(
    graphql(`
      query AdminExternalConnectionsOrganisations {
        organisations {
          id
          name
        }
      }
    `),
  );

  const [filter, setFilter] = useQueryPersistedStateWithSchema({
    useSearchParams,
    schema: z.object({
      sources: z.enum(externalBankAccountDataSourceValues).array().nullish(),
      organisationIds: z.string().array().nullish(),
      statuses: z.enum(externalConnectionStatusValues).array().nullish(),
      sort: z.enum(sortOrders).nullish(),
    }),
    defaultState: {},
  });
  const sortOrder: SortOrder = filter.sort ?? "CONNECTED_AT_DESC";

  let orderBy: ExternalConnectionsOrderBy;
  switch (sortOrder) {
    case "CONNECTED_AT_DESC":
      orderBy = { connectedAt: "DESC" };
      break;
    case "CONNECTED_AT_ASC":
      orderBy = { connectedAt: "ASC" };
      break;
    case "EXPIRES_AT_ASC":
      orderBy = { expiresAt: "ASC" };
      break;
    case "EXPIRES_AT_DESC":
      orderBy = { expiresAt: "DESC" };
      break;
    default:
      neverFunc(sortOrder);
  }

  const handleSortChange = (sortingState: SortingState) => {
    const primarySort = sortingState.at(0);
    let newSortOrder: SortOrder = "CONNECTED_AT_DESC";
    if (primarySort?.id === "connectedAt") {
      newSortOrder = primarySort.desc
        ? "CONNECTED_AT_DESC"
        : "CONNECTED_AT_ASC";
    } else if (primarySort?.id === "expiresAt") {
      newSortOrder = primarySort.desc ? "EXPIRES_AT_DESC" : "EXPIRES_AT_ASC";
    }
    if (newSortOrder !== sortOrder) {
      setFilter({ ...filter, sort: newSortOrder });
    }
  };

  const baseInput = {
    first: 50,
    filter: {
      sources: filter.sources?.length ? filter.sources : undefined,
      organisationIds: filter.organisationIds?.length
        ? filter.organisationIds
        : undefined,
      statuses: filter.statuses?.length ? filter.statuses : undefined,
    },
    orderBy,
  };

  const { fetchMore, loading, data, networkStatus } = useQuery(
    graphql(`
      query AdminExternalConnections($after: String, $first: Int!, $filter: ExternalConnectionFilter, $orderBy: ExternalConnectionsOrderBy) {
        externalConnections(after: $after, first: $first, filter: $filter, orderBy: $orderBy) {
          pageInfo {
            endCursor
            hasNextPage
          }
          externalConnections {
            ...AdminExternalConnectionRow
          }
        }
      }

      fragment AdminExternalConnectionRow on ExternalConnection {
        id
        source
        institutionName
        logoUrl
        connectedAt
        expiresAt
        organisation {
          id
          name
        }
        businessEntity {
          id
          name
          organisation {
            id
            name
          }
        }
        assets {
          id
          name
          currency
          status
          ... on FixedIncomeFundAsset {
            details {
              id
              ... on MorganMoneyExternalAccountDetails {
                unitDrift
              }
            }
          }
        }
        ... on PlaidItem {
          plaidItemStatus: status
        }
        ... on QuilttConnection {
          quilttConnectionStatus: status
        }
        ... on SaltEdgeConnection {
          saltEdgeConnectionStatus: status
          removedAt
        }
        ... on SkriptArrangement {
          skriptArrangementStatus: status
        }
        ... on StripeConnection {
          stripeConnectionStatus: status
          statusReason
          statusChangedAt
          lastSyncedAt
        }
        ... on MercuryConnection {
          mercuryConnectionStatus: status
          statusReason
          statusChangedAt
          lastSyncedAt
        }
        ... on BrexConnection {
          brexConnectionStatus: status
          statusReason
          statusChangedAt
          lastSyncedAt
        }
        ... on RampConnection {
          rampConnectionStatus: status
          statusReason
          statusChangedAt
          lastSyncedAt
        }
        ... on AirwallexConnection {
          airwallexConnectionStatus: status
          statusReason
          statusChangedAt
          lastSyncedAt
        }
        ... on AkahuConnection {
          revokedAt
        }
        ... on SissConnection {
          sissAccountStatus
          statusReason
        }
      }
    `),
    {
      variables: {
        after: null,
        ...baseInput,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const columns = useMemo<ColumnDef<AdminExternalConnectionRowFragment>[]>(
    () => [
      {
        id: "source",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Source" />
        ),
        cell: ({ row }) => getSourceLabel({ source: row.original.source }),
        enableSorting: false,
      },
      {
        id: "institution",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Institution" />
        ),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {row.original.logoUrl && (
              <img className="size-5" alt="" src={row.original.logoUrl} />
            )}
            <span>{row.original.institutionName ?? "-"}</span>
          </div>
        ),
        enableSorting: false,
      },
      {
        id: "businessEntity",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Business Entity" />
        ),
        cell: ({ row }) => {
          if (row.original.businessEntity) {
            return (
              <BusinessEntityColumnCell
                businessEntity={row.original.businessEntity}
              />
            );
          }
          // JPMC and Morgan Money connections belong to an organisation, not a
          // business entity
          return (
            <EntityLink to={`/organisations/${row.original.organisation.id}`}>
              <Txt variant="name">{row.original.organisation.name}</Txt>
            </EntityLink>
          );
        },
        enableSorting: false,
      },
      {
        id: "status",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Status" />
        ),
        cell: ({ row }) => <ConnectionStatus connection={row.original} />,
        enableSorting: false,
      },
      {
        id: "connectedAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Connected" />
        ),
        cell: ({ row }) =>
          row.original.connectedAt ? (
            <DateUI date={row.original.connectedAt} />
          ) : (
            "-"
          ),
        enableSorting: true,
      },
      {
        id: "expiresAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Expires" />
        ),
        cell: ({ row }) => {
          const { expiresAt } = row.original;
          if (!expiresAt) {
            return "-";
          }
          const daysLeft = differenceInDays(expiresAt, new Date());
          let className = "";
          if (daysLeft < 0) {
            className = "text-danger";
          } else if (daysLeft <= 14) {
            className = "text-warning";
          }
          return (
            <span className={className}>
              <DateUI date={expiresAt} />
            </span>
          );
        },
        enableSorting: true,
      },
      {
        id: "lastSyncedAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Last synced" />
        ),
        cell: ({ row }) => {
          if (!("lastSyncedAt" in row.original) || !row.original.lastSyncedAt) {
            return "-";
          }
          const { lastSyncedAt } = row.original;
          // syncs run hourly, so a day without one means something is stuck
          const stale = differenceInHours(new Date(), lastSyncedAt) >= 24;
          return (
            <span className={stale ? "text-warning" : ""}>
              <DateUI date={lastSyncedAt} variant="DATETIME" delta />
            </span>
          );
        },
        enableSorting: false,
      },
      {
        id: "accounts",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Accounts" />
        ),
        cell: ({ row }) => {
          const { assets } = row.original;
          if (assets.length === 0) {
            return "0";
          }
          return (
            <Popover>
              <PopoverTrigger asChild>
                <Button size="sm" variant="outline">
                  {assets.length}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 p-2">
                <ul className="flex flex-col gap-1">
                  {assets.map((asset) => (
                    <li key={asset.id}>
                      <Link
                        to="/assets/$assetId"
                        params={{ assetId: asset.id }}
                        className="flex items-center justify-between gap-2 rounded-md px-2 py-1 text-sm hover:bg-secondary-interactive-hover"
                      >
                        <span className="truncate">
                          {asset.name} ({asset.currency})
                        </span>
                        {asset.__typename === "FixedIncomeFundAsset" &&
                          asset.details?.__typename ===
                            "MorganMoneyExternalAccountDetails" &&
                          asset.details.unitDrift?.abs().greaterThan(0.001) && (
                            <StatusBase
                              color="warning"
                              label={`Drift ${asset.details.unitDrift.toString()} units`}
                            />
                          )}
                        <StatusUI status={asset.status} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </PopoverContent>
            </Popover>
          );
        },
        enableSorting: false,
      },
    ],
    [],
  );

  const nextCursor = data?.externalConnections.pageInfo.hasNextPage
    ? (data.externalConnections.pageInfo.endCursor ?? undefined)
    : undefined;

  const rows = data?.externalConnections.externalConnections ?? [];

  const sourceOptions = connectionSources.map((source) => ({
    value: source,
    label: getSourceLabel({ source }),
  }));

  const statusOptions = externalConnectionStatusValues.map((status) => ({
    value: status,
    label: formatStatusLabel({ status }),
  }));

  const organisationOptions =
    organisationsData?.organisations?.map((o) => ({
      value: o.id,
      label: o.name,
    })) ?? [];

  return (
    <AdminPageContainer testId="admin-external-connections">
      <AdminPageHeader
        title="External Connections"
        subtitle="Customer connections to external bank data providers."
      />
      <div className="flex flex-col gap-4">
        <div className="flex flex-row items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={filter.sources?.length ? "outline" : "secondary"}
                size="sm"
              >
                <FacetedFilterSummaryContent
                  title="Source"
                  options={sourceOptions}
                  selectedValues={filter.sources ?? []}
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="start">
              <ControlledFacetedFilterControl
                options={sourceOptions}
                selectedValues={filter.sources ?? []}
                onChange={(newSelectedValues) => {
                  setFilter({
                    ...filter,
                    sources: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={filter.statuses?.length ? "outline" : "secondary"}
                size="sm"
              >
                <FacetedFilterSummaryContent
                  title="Status"
                  options={statusOptions}
                  selectedValues={filter.statuses ?? []}
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="start">
              <ControlledFacetedFilterControl
                options={statusOptions}
                selectedValues={filter.statuses ?? []}
                onChange={(newSelectedValues) => {
                  setFilter({
                    ...filter,
                    statuses: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={
                  filter.organisationIds?.length ? "outline" : "secondary"
                }
                size="sm"
              >
                <FacetedFilterSummaryContent
                  title="Organisation"
                  options={organisationOptions}
                  selectedValues={filter.organisationIds ?? []}
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
              <ControlledFacetedFilterControl
                options={organisationOptions}
                selectedValues={filter.organisationIds ?? []}
                onChange={(newSelectedValues) => {
                  setFilter({
                    ...filter,
                    organisationIds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            </PopoverContent>
          </Popover>
        </div>
        <DataTable
          data={rows}
          columns={columns}
          isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
          manualPagination={true}
          manualSorting
          maxMultiSortColCount={1}
          onSortingChange={handleSortChange}
          columnSortProps={{
            id: sortOrder.startsWith("EXPIRES_AT")
              ? "expiresAt"
              : "connectedAt",
            desc: sortOrder.endsWith("_DESC"),
          }}
          hidePagination={true}
          pageSize={10_000}
          className="overflow-hidden border"
        />
        <div className="mt-4 flex w-full flex-row justify-center">
          {nextCursor ? (
            <Button
              onClick={() =>
                fetchMore({ variables: { ...baseInput, after: nextCursor } })
              }
              variant="outline"
              disabled={loading}
              loading={loading || networkStatus === NetworkStatus.fetchMore}
            >
              Load more
            </Button>
          ) : (
            !!rows.length && (
              <p className="text-secondary text-sm">No more data</p>
            )
          )}
        </div>
      </div>
    </AdminPageContainer>
  );
}

function ConnectionStatus({
  connection,
}: {
  connection: AdminExternalConnectionRowFragment;
}) {
  let providerStatus: string | null;
  let statusReason: string | null = null;
  let statusChangedAt: Date | null = null;
  switch (connection.__typename) {
    case "PlaidItem":
      providerStatus = connection.plaidItemStatus;
      break;
    case "QuilttConnection":
      providerStatus = connection.quilttConnectionStatus;
      break;
    case "SaltEdgeConnection":
      providerStatus = connection.removedAt
        ? "REMOVED"
        : connection.saltEdgeConnectionStatus;
      break;
    case "SkriptArrangement":
      providerStatus = connection.skriptArrangementStatus;
      break;
    case "StripeConnection":
      providerStatus = connection.stripeConnectionStatus;
      statusReason = connection.statusReason ?? null;
      statusChangedAt = connection.statusChangedAt ?? null;
      break;
    case "MercuryConnection":
      providerStatus = connection.mercuryConnectionStatus;
      statusReason = connection.statusReason ?? null;
      statusChangedAt = connection.statusChangedAt ?? null;
      break;
    case "BrexConnection":
      providerStatus = connection.brexConnectionStatus;
      statusReason = connection.statusReason ?? null;
      statusChangedAt = connection.statusChangedAt ?? null;
      break;
    case "RampConnection":
      providerStatus = connection.rampConnectionStatus;
      statusReason = connection.statusReason ?? null;
      statusChangedAt = connection.statusChangedAt ?? null;
      break;
    case "AirwallexConnection":
      providerStatus = connection.airwallexConnectionStatus;
      statusReason = connection.statusReason ?? null;
      statusChangedAt = connection.statusChangedAt ?? null;
      break;
    case "AkahuConnection":
      providerStatus = connection.revokedAt ? "REVOKED" : "ACTIVE";
      break;
    case "SissConnection":
      providerStatus = connection.sissAccountStatus;
      statusReason = connection.statusReason ?? null;
      break;
    case "JpmcConnection":
    case "MorganMoneyConnection":
      providerStatus = null;
      break;
    default:
      return neverFunc(connection);
  }

  if (!providerStatus) {
    return "-";
  }

  let tooltipContent: ReactNode;
  if (statusReason || statusChangedAt) {
    tooltipContent = (
      <div className="flex max-w-sm flex-col gap-1">
        {statusReason && <span className="break-words">{statusReason}</span>}
        {statusChangedAt && (
          <span>
            Since <DateUI date={statusChangedAt} variant="DATETIME" />
          </span>
        )}
      </div>
    );
  }

  const knownStatus = statuses.find((s): s is Status => s === providerStatus);
  if (knownStatus) {
    return (
      <StatusUI
        status={knownStatus}
        tooltip={!!tooltipContent}
        tooltipContent={tooltipContent}
      />
    );
  }

  return (
    <StatusBase
      color="neutral"
      label={formatStatusLabel({ status: providerStatus })}
      tooltip={!!tooltipContent}
      tooltipContent={tooltipContent}
    />
  );
}

function formatStatusLabel({ status }: { status: string }): string {
  return status
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getSourceLabel({
  source,
}: {
  source: ExternalBankAccountDataSource;
}): string {
  switch (source) {
    case "AKAHU":
      return "Akahu";
    case "QUILTT":
      return "Quiltt";
    case "SALT_EDGE":
      return "Salt Edge";
    case "SISS":
      return "SISS";
    case "SKRIPT":
      return "Skript";
    case "STRIPE":
      return "Stripe";
    case "MERCURY":
      return "Mercury";
    case "AIRWALLEX":
      return "Airwallex";
    case "BREX":
      return "Brex";
    case "RAMP":
      return "Ramp";
    case "PLAID":
      return "Plaid";
    case "JPMC":
      return "JPMC";
    case "MORGAN_MONEY":
      return "J.P. Morgan Asset Management";
    case "MANUAL_ENTRY":
      return "Manual entry";
    default:
      return neverFunc(source);
  }
}
